export const SERVER_API = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

export async function getSystemStatus() {
  try {
    const response = await fetch(`${SERVER_API}/system/status`, { credentials: "include" });
    return response.json();
  } catch {
    return { readonly: false };
  }
}

const defaults = { credentials: "include" };

function handleUnauthorized(response) {
  if (response.status === 401) {
    window.location.href = "/login";
    throw new Error("Session expired");
  }
}

export async function getEntities(route, params = "") {
  const response = await fetch(`${SERVER_API}/${route}${params}`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function getEntity(route, id) {
  const response = await fetch(`${SERVER_API}/${route}/${id}`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function updateEntity(route, entity) {
  try {
    await fetch(`${SERVER_API}/${route}/${entity.id}`, {
      ...defaults,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(entity),
    });
  } catch (err) {
    console.error(err);
  }
}

export async function addEntity(route, entity) {
  try {
    const response = await fetch(`${SERVER_API}/${route}`, {
      ...defaults,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(entity),
    });
    console.log(response);
  } catch (err) {
    console.error(err);
  }
}

export async function deleteEntity(route, entity) {
  try {
    await fetch(`${SERVER_API}/${route}/${entity.id}`, {
      ...defaults,
      method: "DELETE",
    });
  } catch (err) {
    console.error(err);
  }
}

export async function downloadDistributionByBannerByItem({
  manufacturerId,
  fromDate,
  toDate,
  bannerIds,
  territoryIds,
  productIds,
}) {
  const params = new URLSearchParams({ manufacturerId, fromDate, toDate });
  bannerIds.forEach((id) => params.append("bannerIds", id));
  territoryIds.forEach((id) => params.append("territoryIds", id));
  productIds.forEach((id) => params.append("productIds", id));

  const response = await fetch(
    `${SERVER_API}/reports/distribution-by-banner-by-item?${params}`,
    defaults
  );
  handleUnauthorized(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Distribution_by_Banner_by_Item.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadDistributionSummary({
  manufacturerId,
  fromDate,
  toDate,
  bannerIds,
  territoryIds,
  productIds,
}) {
  const params = new URLSearchParams({ manufacturerId, fromDate, toDate });
  (bannerIds ?? []).forEach((id) => params.append("bannerIds", id));
  (territoryIds ?? []).forEach((id) => params.append("territoryIds", id));
  (productIds ?? []).forEach((id) => params.append("productIds", id));

  const response = await fetch(
    `${SERVER_API}/reports/stock-level-summary?${params}`,
    defaults
  );
  handleUnauthorized(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Distribution_Summary.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadTotalDistributionByBanner({
  manufacturerId,
  fromDate,
  toDate,
  bannerIds,
  productIds,
  status,
}) {
  const params = new URLSearchParams({ manufacturerId, fromDate, toDate });
  if (status) params.set("status", status);
  (bannerIds ?? []).forEach((id) => params.append("bannerIds", id));
  (productIds ?? []).forEach((id) => params.append("productIds", id));

  const response = await fetch(
    `${SERVER_API}/reports/total-distribution-by-banner?${params}`,
    defaults
  );
  handleUnauthorized(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Total_Distribution_by_Banner.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export async function getStoresForDashboard(bannerIds = []) {
  const params = new URLSearchParams();
  bannerIds.forEach((id) => params.append("bannerIds", id));
  const response = await fetch(`${SERVER_API}/stores/dashboard?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error("Failed to load stores");
  return response.json();
}

export async function getDashboardData({ manufacturerId, months = [], provinceIds = [], productIds = [], bannerIds = [], storeIds = [] }) {
  const params = new URLSearchParams({ manufacturerId });
  months.forEach((m) => params.append("months", m));
  provinceIds.forEach((id) => params.append("provinceIds", id));
  productIds.forEach((id) => params.append("productIds", id));
  bannerIds.forEach((id) => params.append("bannerIds", id));
  storeIds.forEach((id) => params.append("storeIds", id));
  const response = await fetch(`${SERVER_API}/dashboard?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Dashboard request failed: ${response.status}`);
  return response.json();
}

export async function getHotSheets() {
  const response = await fetch(`${SERVER_API}/hot-sheets`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function getHotSheet(id) {
  const response = await fetch(`${SERVER_API}/hot-sheets/${id}`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function addHotSheet(hotSheet) {
  await fetch(`${SERVER_API}/hot-sheets`, {
    ...defaults,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(hotSheet),
  });
}

export async function updateHotSheet(id, hotSheet) {
  await fetch(`${SERVER_API}/hot-sheets/${id}`, {
    ...defaults,
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(hotSheet),
  });
}

export async function deleteHotSheet(id) {
  await fetch(`${SERVER_API}/hot-sheets/${id}`, { ...defaults, method: "DELETE" });
}

export async function getBookings() {
  const response = await fetch(`${SERVER_API}/bookings`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function getBooking(id) {
  const response = await fetch(`${SERVER_API}/bookings/${id}`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function addBooking(booking) {
  await fetch(`${SERVER_API}/bookings`, {
    ...defaults,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(booking),
  });
}

export async function updateBooking(id, booking) {
  await fetch(`${SERVER_API}/bookings/${id}`, {
    ...defaults,
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(booking),
  });
}

export async function deleteBooking(id) {
  await fetch(`${SERVER_API}/bookings/${id}`, { ...defaults, method: "DELETE" });
}

export async function uploadBookingItemPicture(bookingId, itemId, file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${SERVER_API}/bookings/${bookingId}/items/${itemId}/picture`, {
    ...defaults,
    method: "POST",
    body: formData,
  });
  handleUnauthorized(response);
  return response.json();
}

export async function getBannerByMonthData({ manufacturerId, month, bannerIds, productIds, provinceIds, territoryIds, representativeIds, status }) {
  const params = new URLSearchParams({ manufacturerId, month });
  if (status) params.set("status", status);
  (bannerIds ?? []).forEach((id) => params.append("bannerIds", id));
  (productIds ?? []).forEach((id) => params.append("productIds", id));
  (provinceIds ?? []).forEach((id) => params.append("provinceIds", id));
  (territoryIds ?? []).forEach((id) => params.append("territoryIds", id));
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  const response = await fetch(`${SERVER_API}/reports/banner-by-month?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function exportBannerByMonth({ manufacturerId, month, bannerIds, productIds, provinceIds, territoryIds, representativeIds, status }) {
  const params = new URLSearchParams({ manufacturerId, month });
  if (status) params.set("status", status);
  (bannerIds ?? []).forEach((id) => params.append("bannerIds", id));
  (productIds ?? []).forEach((id) => params.append("productIds", id));
  (provinceIds ?? []).forEach((id) => params.append("provinceIds", id));
  (territoryIds ?? []).forEach((id) => params.append("territoryIds", id));
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  const response = await fetch(`${SERVER_API}/reports/banner-by-month/export?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Distribution_By_Banner_By_Month_${month}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function exportPicturesData({ manufacturerId, fromDate, toDate, bannerIds = [], provinceIds = [], territoryIds = [], representativeIds = [], hashtags = [] }) {
  const params = new URLSearchParams({ manufacturerId });
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  bannerIds.forEach((id) => params.append("bannerIds", id));
  provinceIds.forEach((id) => params.append("provinceIds", id));
  territoryIds.forEach((id) => params.append("territoryIds", id));
  representativeIds.forEach((id) => params.append("representativeIds", id));
  hashtags.forEach((h) => params.append("hashtags", h));
  const response = await fetch(`${SERVER_API}/reports/pictures/export?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Pictures_Report.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export async function getDashboardMerchandisingData({ manufacturerId, months = [], bannerId, provinceIds = [] }) {
  const params = new URLSearchParams({ manufacturerId });
  months.forEach((m) => params.append("months", m));
  if (bannerId) params.set("bannerId", bannerId);
  provinceIds.forEach((id) => params.append("provinceIds", id));
  const response = await fetch(`${SERVER_API}/dashboard/merchandising?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getMonthlyMerchandisingData({ manufacturerId, month, bannerId, provinceIds, territoryIds, representativeIds }) {
  const params = new URLSearchParams({ manufacturerId, month });
  if (bannerId) params.set("bannerId", bannerId);
  (provinceIds ?? []).forEach((id) => params.append("provinceIds", id));
  (territoryIds ?? []).forEach((id) => params.append("territoryIds", id));
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  const response = await fetch(`${SERVER_API}/reports/monthly-merchandising?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function exportMonthlyMerchandising({ manufacturerId, month, bannerId, provinceIds, territoryIds, representativeIds }) {
  const params = new URLSearchParams({ manufacturerId, month });
  if (bannerId) params.set("bannerId", bannerId);
  (provinceIds ?? []).forEach((id) => params.append("provinceIds", id));
  (territoryIds ?? []).forEach((id) => params.append("territoryIds", id));
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  const response = await fetch(`${SERVER_API}/reports/monthly-merchandising/export?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Monthly_Merchandising_${month}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function getHashtagsForManufacturer(manufacturerId) {
  const response = await fetch(`${SERVER_API}/photoHashtags/manufacturer/${manufacturerId}`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function getPicturesData({ manufacturerId, fromDate, toDate, bannerIds, provinceIds, territoryIds, representativeIds, hashtags }) {
  const params = new URLSearchParams({ manufacturerId });
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  (bannerIds ?? []).forEach((id) => params.append("bannerIds", id));
  (provinceIds ?? []).forEach((id) => params.append("provinceIds", id));
  (territoryIds ?? []).forEach((id) => params.append("territoryIds", id));
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  (hashtags ?? []).forEach((h) => params.append("hashtags", h));
  const response = await fetch(`${SERVER_API}/reports/pictures?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function createPicturesShare({ manufacturerId, fromDate, toDate, bannerIds, provinceIds, territoryIds, representativeIds, hashtags }) {
  const response = await fetch(`${SERVER_API}/reports/pictures/share`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ manufacturerId, fromDate, toDate, bannerIds, provinceIds, territoryIds, representativeIds, hashtags: hashtags ?? [] }),
  });
  if (!response.ok) throw new Error("Failed to create share link");
  return response.json();
}

export async function getSharedPictures(token) {
  const response = await fetch(`${SERVER_API}/reports/pictures/shared/${token}`);
  if (!response.ok) throw new Error("Report not found");
  return response.json(); // { fromDate, toDate, photos }
}

export async function createDashboardShare({ manufacturerId, manufacturerName, months, provinceIds, productIds, bannerIds, storeIds = [], storeNames = [] }) {
  const response = await fetch(`${SERVER_API}/dashboard/share`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ manufacturerId, manufacturerName, months, provinceIds, productIds, bannerIds, storeIds, storeNames }),
  });
  if (!response.ok) throw new Error("Failed to create share link");
  return response.json();
}

export async function createPicturesLinkFromDashboard(dashboardToken, { months = [], provinceIds = [] }) {
  const response = await fetch(`${SERVER_API}/dashboard/shared/${dashboardToken}/pictures-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ months, provinceIds }),
  });
  if (!response.ok) throw new Error("Failed to create pictures link");
  return response.json(); // { token }
}

export async function getSharedDashboard(token) {
  const response = await fetch(`${SERVER_API}/dashboard/shared/${token}`);
  if (!response.ok) throw new Error("Dashboard not found");
  return response.json(); // { manufacturerName, visibleCharts, defaultMonths }
}

export async function getSharedDashboardData(token, { months = [], provinceIds = [], bannerIds = [] }) {
  const params = new URLSearchParams();
  months.forEach((m) => params.append("months", m));
  provinceIds.forEach((id) => params.append("provinceIds", id));
  bannerIds.forEach((id) => params.append("bannerIds", id));
  const response = await fetch(`${SERVER_API}/dashboard/shared/${token}/data?${params}`);
  if (!response.ok) throw new Error("Failed to load dashboard data");
  return response.json();
}

export async function getDashboardConfigs() {
  const response = await fetch(`${SERVER_API}/dashboard/configs`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error("Failed to load configs");
  return response.json();
}

export async function saveDashboardConfig(config) {
  const isNew = !config.id;
  const url = isNew
    ? `${SERVER_API}/dashboard/configs`
    : `${SERVER_API}/dashboard/configs/${config.id}`;
  const response = await fetch(url, {
    method: isNew ? "POST" : "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(config),
  });
  if (!response.ok) throw new Error("Failed to save config");
  return response.json();
}

export async function deleteDashboardConfig(id) {
  const response = await fetch(`${SERVER_API}/dashboard/configs/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to delete config");
}

export async function getManufacturerPreset(id) {
  const response = await fetch(`${SERVER_API}/manufacturer-presets/${id}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error("Failed to load preset");
  return response.json();
}

export async function getManufacturerPresets() {
  const response = await fetch(`${SERVER_API}/manufacturer-presets`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error("Failed to load presets");
  return response.json();
}

export async function saveManufacturerPreset(preset) {
  const isNew = !preset.id;
  const url = isNew
    ? `${SERVER_API}/manufacturer-presets`
    : `${SERVER_API}/manufacturer-presets/${preset.id}`;
  const response = await fetch(url, {
    method: isNew ? "POST" : "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(preset),
  });
  if (!response.ok) throw new Error("Failed to save preset");
  return response.json();
}

export async function getPresetProducts(manufacturerId, bannerIds, provinceIds) {
  const params = new URLSearchParams({ manufacturerId });
  if (bannerIds && bannerIds.length > 0) params.set("bannerIds", bannerIds.join(","));
  if (provinceIds && provinceIds.length > 0) params.set("provinceIds", provinceIds.join(","));
  const response = await fetch(`${SERVER_API}/manufacturer-presets/products?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error("Failed to load products");
  return response.json();
}

export async function deleteManufacturerPreset(id) {
  const response = await fetch(`${SERVER_API}/manufacturer-presets/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to delete preset");
}

export async function getRepresentativesWithSync() {
  const response = await fetch(`${SERVER_API}/representatives/with-sync`, defaults);
  handleUnauthorized(response);
  return response.json();
}

export async function downloadDistributionAudit({
  bannerIds,
  manufacturerId,
  fromDate,
  toDate,
  representativeIds,
  productIds,
}) {
  const params = new URLSearchParams({ manufacturerId, fromDate, toDate });
  (bannerIds ?? []).forEach((id) => params.append("bannerIds", id));
  representativeIds.forEach((id) => params.append("representativeIds", id));
  productIds.forEach((id) => params.append("productIds", id));

  const response = await fetch(`${SERVER_API}/reports/distribution-audit?${params}`, defaults);
  handleUnauthorized(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Distribution_Audit.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export async function getVisitReport({ fromDate, toDate, representativeIds } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  const response = await fetch(`${SERVER_API}/reports/visits?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getVisitProducts(visitId) {
  const response = await fetch(`${SERVER_API}/reports/visits/${encodeURIComponent(visitId)}/products`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getTaskReport(taskId, { fromDate, toDate, representativeIds } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  const response = await fetch(`${SERVER_API}/reports/tasks/${taskId}?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getCallTime({ fromDate, toDate, flagThreshold, representativeIds, manufacturerIds } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  if (flagThreshold != null) params.set("flagThreshold", flagThreshold);
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  (manufacturerIds ?? []).forEach((id) => params.append("manufacturerIds", id));
  const response = await fetch(`${SERVER_API}/performance/call-time?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getPhotoCapture({ fromDate, toDate, flagThreshold, representativeIds, manufacturerIds } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  if (flagThreshold != null) params.set("flagThreshold", flagThreshold);
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  (manufacturerIds ?? []).forEach((id) => params.append("manufacturerIds", id));
  const response = await fetch(`${SERVER_API}/performance/photo-capture?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getRepDistribution({ fromDate, toDate, representativeIds, manufacturerIds } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  (manufacturerIds ?? []).forEach((id) => params.append("manufacturerIds", id));
  const response = await fetch(`${SERVER_API}/performance/rep-distribution?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function getCallCompletion({ fromDate, toDate, threshold, representativeIds, manufacturerIds } = {}) {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  if (threshold != null) params.set("threshold", threshold);
  (representativeIds ?? []).forEach((id) => params.append("representativeIds", id));
  (manufacturerIds ?? []).forEach((id) => params.append("manufacturerIds", id));
  const response = await fetch(`${SERVER_API}/performance/call-completion?${params}`, defaults);
  handleUnauthorized(response);
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

export async function downloadBookingExport(bookingId) {
  const response = await fetch(`${SERVER_API}/bookings/${bookingId}/export`, defaults);
  handleUnauthorized(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Booking_${bookingId}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadDistributionDetail({
  bannerIds,
  manufacturerId,
  fromDate,
  toDate,
  representativeIds,
  productIds,
  status,
}) {
  const params = new URLSearchParams({ manufacturerId, fromDate, toDate });
  if (status) params.set("status", status);
  (bannerIds || []).forEach((id) => params.append("bannerIds", id));
  (representativeIds || []).forEach((id) => params.append("representativeIds", id));
  (productIds || []).forEach((id) => params.append("productIds", id));

  const response = await fetch(`${SERVER_API}/reports/distribution-detail?${params}`, defaults);
  handleUnauthorized(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Stock_Level_Audit.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}
